import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import EmptyRows from "../../../components/reusableComponent/EmptyRows";
import TableModal from "../../../components/reusableComponent/TableModal";
import { useGetTicketClickToExpandQuery } from "../../../Services/dashboard/TicketDashboard";

interface IProps {
  data?: any; // full tabData object — reads data.tickets_non_actionable
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
  workflowId?: any;
}

const CONFIG = [
  { key: "false", label: "Actionable",    color: "#6366f1" },
  { key: "true",  label: "Non-Actionable", color: "#f97316" },
];

const ActionableVsNonActionable: React.FC<IProps> = ({
  data,
  startDate,
  endDate,
  teamId,
  workflowId,
}) => {
  const navigate = useNavigate();
  const [chartData, setChartData]     = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const [allZero, setAllZero]         = useState(false);
  const [tableModalOpen, setTableModalOpen]   = useState(false);
  const [selectedKey, setSelectedKey]         = useState<string | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);

  // recharts cast (same pattern as rest of your codebase)
  const PieChartContainer        = PieChart        as any;
  const PieContainer             = Pie             as any;
  const CellContainer            = Cell            as any;
  const ResponsiveChartContainer = ResponsiveContainer as any;
  const LegendContainer          = Legend          as any;
  const TooltipContainer         = Tooltip         as any;

  // ── Parse from tickets_non_actionable ─────────────────────────────────────
  useEffect(() => {
    const source      = data?.tickets_non_actionable;
    const actionable    = Number(source?.actionable_count    ?? 0);
    const nonActionable = Number(source?.non_actionable_count ?? 0);

    const normalized = [
      { name: "Actionable",     value: actionable,    fill: CONFIG[0].color, key: CONFIG[0].key },
      { name: "Non-Actionable", value: nonActionable, fill: CONFIG[1].color, key: CONFIG[1].key },
    ];

    setAllZero(normalized.every((d) => d.value === 0));
    setActiveIndex(undefined);
    setChartData(normalized);
  }, [data]);

  const totalCount = chartData.reduce((sum, item) => sum + item.value, 0);
  const clickable  = !!(teamId && startDate && endDate);

  // ── Click-to-expand query ─────────────────────────────────────────────────
  const clickToExpandParams =
    tableModalOpen && selectedKey && teamId && startDate && endDate
      ? {
          graph_name:  "tickets_non_actionable",
          value:       selectedKey,
          start_date:  startDate,
          end_date:    endDate,
          team_id:     teamId,
          page:        clickToExpandPage,
          page_size:   10,
          workflow_id: workflowId || null,
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetTicketClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "", value: "", start_date: "", end_date: "", team_id: "",
      },
      { skip: !clickToExpandParams },
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw =
      (clickToExpandData as any)?.results ??
      (clickToExpandData as any)?.data ??
      clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count      = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page       = d.page ?? clickToExpandPage;
    const pageSize   = d.page_size ?? 10;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedKey]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSegmentClick = (index: number) => {
    if (!clickable) return;
    const key = chartData[index]?.key;
    if (key) {
      setSelectedKey(key);
      setTableModalOpen(true);
      setClickToExpandPage(1);
    }
  };

  const handleCloseModal = () => {
    setTableModalOpen(false);
    setSelectedKey(null);
    setClickToExpandPage(1);
  };

  // ── Custom legend ─────────────────────────────────────────────────────────
  const CustomLegend = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {chartData.map((item, index) => {
        const percentage =
          totalCount > 0 ? ((item.value / totalCount) * 100).toFixed(0) : 0;
        const isActive = activeIndex === index;

        return (
          <div
            key={item.name}
            role="button"
            onClick={() => handleSegmentClick(index)}
            onMouseEnter={() => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(undefined)}
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              cursor: clickable ? "pointer" : "default",
              background: isActive ? `${item.fill}10` : "#f8fafc",
              border: `1.5px solid ${isActive ? item.fill : "transparent"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "all 0.2s ease",
              transform: isActive ? "scale(1.02)" : "scale(1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: item.fill,
                  boxShadow: isActive ? `0 0 8px ${item.fill}80` : "none",
                }}
              />
              <span
                style={{
                  color: "#374151",
                  fontWeight: isActive ? "600" : "500",
                  fontSize: "13px",
                  marginRight: "5px",
                }}
              >
                {item.name}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: "500" }}>
                {percentage}%
              </span>
              <strong
                style={{
                  color: isActive ? item.fill : "#1f2937",
                  fontSize: "14px",
                  minWidth: "30px",
                  textAlign: "right",
                }}
              >
                {item.value}
              </strong>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ── Custom tooltip ────────────────────────────────────────────────────────
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const item = payload[0];
    return (
      <div
        style={{
          background: "white",
          padding: "8px 12px",
          borderRadius: 6,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          border: `2px solid ${item.payload.fill}`,
        }}
      >
        <div style={{ color: item.payload.fill, fontWeight: "600", fontSize: "13px", marginBottom: 2 }}>
          {item.name}
        </div>
        <div style={{ color: "#6b7280", fontSize: "12px" }}>
          {item.payload.value} tickets
        </div>
        {clickable && (
          <div style={{ color: "#94a3b8", fontSize: "11px", marginTop: 4 }}>
            Click to see details
          </div>
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="card card-flush h-lg-100">
      <div className="card-header pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="fs-2 fw-bold text-gray-800 me-2 lh-1 ls-n2">
            {`Actionable vs Non-Actionable: ${totalCount}`}
          </span>
          {clickable && (
            <span className="text-muted mt-1 fw-semibold fs-7">
              Click a segment to view tickets
            </span>
          )}
        </h3>
      </div>

      <div className="card-body pt-5">
        {allZero ? (
          <div style={{ height: 270, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <EmptyRows value="" />
          </div>
        ) : (
          <ResponsiveChartContainer width="100%" height={270}>
            <PieChartContainer>
              <PieContainer
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius="45%"
                outerRadius="75%"
                dataKey="value"
                onClick={(_: any, index: number) => handleSegmentClick(index)}
                onMouseEnter={(_: any, index: number) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(undefined)}
                paddingAngle={3}
                animationBegin={0}
                animationDuration={600}
              >
                {chartData.map((d, i) => (
                  <CellContainer
                    key={i}
                    fill={d.fill}
                    strokeWidth={activeIndex === i ? 4 : 2}
                    stroke={activeIndex === i ? "white" : "#ffffff"}
                    style={{
                      filter:
                        activeIndex === i
                          ? `drop-shadow(0 4px 12px ${d.fill}60)`
                          : "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                      transition: "all 0.3s ease",
                      transform: activeIndex === i ? "scale(1.05)" : "scale(1)",
                      transformOrigin: "center",
                      cursor: clickable ? "pointer" : "default",
                    }}
                  />
                ))}
              </PieContainer>

              <TooltipContainer content={<CustomTooltip />} />
              <LegendContainer
                content={<CustomLegend />}
                layout="vertical"
                align="right"
                verticalAlign="middle"
                wrapperStyle={{ paddingLeft: 20 }}
              />
            </PieChartContainer>
          </ResponsiveChartContainer>
        )}
      </div>

      {/* ── Click-to-expand modal ── */}
      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={handleCloseModal}
        title={
          selectedKey
            ? `Tickets – ${CONFIG.find((c) => c.key === selectedKey)?.label ?? selectedKey}`
            : "Tickets"
        }
        contentLabel="Actionable vs Non-Actionable click-to-expand"
        emptyMessage="No tickets in this segment."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx: number) => row?.ticket_id ?? row?.id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const ticketId = row?.ticket_id ?? row?.id;
          if (ticketId != null) {
            handleCloseModal();
            navigate(`/ticket/information/${ticketId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={[
          { label: "Ticket ID",     render: (row: any) => row?.ticket_id         ?? "—" },
          { label: "Title",         render: (row: any) => row?.title              ?? "—" },
          { label: "Channel",       render: (row: any) => row?.ticket_channel     ?? "—" },
          { label: "Contact Name",  render: (row: any) => row?.contact_name       ?? "—" },
          { label: "Contact Email", render: (row: any) => row?.contact_email      ?? "—" },
          { label: "Contact Phone", render: (row: any) => row?.contact_phone      ?? "—" },
          { label: "Assignee",      render: (row: any) => row?.assignee_name      ?? "—" },
          { label: "Disposition",   render: (row: any) => row?.disposition_topic  ?? "—" },
          {
            label: "Created",
            render: (row: any) => {
              const raw = row?.created;
              if (!raw) return "—";
              const d = new Date(raw);
              if (Number.isNaN(d.getTime())) return "—";
              const dd   = String(d.getDate()).padStart(2, "0");
              const mm   = String(d.getMonth() + 1).padStart(2, "0");
              const yyyy = d.getFullYear();
              const hh   = String(d.getHours()).padStart(2, "0");
              const min  = String(d.getMinutes()).padStart(2, "0");
              const ss   = String(d.getSeconds()).padStart(2, "0");
              return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
            },
          },
          {
            label: "Updated",
            render: (row: any) => {
              const raw = row?.updated;
              if (!raw) return "—";
              const d = new Date(raw);
              if (Number.isNaN(d.getTime())) return "—";
              const dd   = String(d.getDate()).padStart(2, "0");
              const mm   = String(d.getMonth() + 1).padStart(2, "0");
              const yyyy = d.getFullYear();
              const hh   = String(d.getHours()).padStart(2, "0");
              const min  = String(d.getMinutes()).padStart(2, "0");
              const ss   = String(d.getSeconds()).padStart(2, "0");
              return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
            },
          },
        ]}
      />
    </div>
  );
};

export default ActionableVsNonActionable;